/* NAMVEXO NFC URL validation — scoped, observer-free extension */
(() => {
  'use strict';
  const KEY = '__nmvxNfcPersonalizationV1__';
  const SEL = '[data-nmvx-nfc]';
  if (window[KEY]) return window[KEY]();

  const cards = new WeakMap();
  let guards = false;
  let lifecycle = false;

  const validUrl = (raw) => {
    const value = String(raw || '').trim();
    if (!/^https?:\/\//i.test(value) || /\s/.test(value)) return false;
    try {
      const url = new URL(value);
      return /https?:/.test(url.protocol) && url.hostname.includes('.');
    } catch (_) { return false; }
  };

  const cardForForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return null;
    const id = form.getAttribute('id');
    return id ? [...document.querySelectorAll(SEL)].find((c) => !c.hidden && c.dataset.formId === id) || null : null;
  };

  const cardForControl = (control) => {
    const direct = cardForForm(control.closest('form'));
    if (direct) return direct;
    const root = control.closest('.shopify-section') || control.closest('product-information');
    const local = root && [...root.querySelectorAll(SEL)].find((c) => !c.hidden);
    if (local) return local;
    const visible = [...document.querySelectorAll(SEL)].filter((c) => !c.hidden);
    return visible.length === 1 ? visible[0] : null;
  };

  const stopInvalid = (card, event) => {
    const api = cards.get(card);
    if (!api || api.sync(true).valid) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    api.report();
    return true;
  };

  const installGuards = () => {
    if (guards) return;
    guards = true;
    document.addEventListener('click', (event) => {
      try {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const control = target.closest('[ref="addToCartButton"],button[name="add"],add-to-cart-component button,sticky-add-to-cart button');
        const card = control && cardForControl(control);
        if (card) stopInvalid(card, event);
      } catch (error) { console.error('[NAMVEXO NFC] Safe click guard error.', error); }
    }, true);
    document.addEventListener('submit', (event) => {
      try {
        const card = cardForForm(event.target);
        if (card) stopInvalid(card, event);
      } catch (error) { console.error('[NAMVEXO NFC] Safe submit guard error.', error); }
    }, true);
  };

  const init = (card) => {
    if (cards.has(card)) {
      const api = cards.get(card);
      api.place();
      return api.queue();
    }
    const input = card.querySelector('[data-nmvx-primary]');
    const form = document.getElementById(card.dataset.formId || '');
    if (!(input instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) return;

    const context = card.closest('.product-details') || card.closest('product-information') || card.closest('.shopify-section') || document;
    const placeCard = () => {
      const buyButtons = context.querySelector?.('.buy-buttons-block');
      if (buyButtons && card.nextElementSibling !== buyButtons) buyButtons.insertAdjacentElement('beforebegin', card);
    };
    placeCard();

    const root = card.closest('.product-details') || card.closest('product-information') || card.closest('.shopify-section') || context;
    const summary = card.querySelector('[data-nmvx-summary]');
    const error = card.querySelector('[data-nmvx-error]');
    const aborter = new AbortController();
    let attempted = false;
    let frame = 0;
    let follow = 0;

    const readiness = () => card.querySelector('[data-nmvx-readiness]');
    const message = (value) => value
      ? 'Enter a complete destination URL beginning with https:// or http://.'
      : card.dataset.requiredMessage;

    const setSummary = (value, valid) => {
      if (!summary) return;
      summary.replaceChildren();
      summary.classList.add('nmvx-personalization__summary-list');
      const chip = document.createElement('span');
      chip.className = 'nmvx-personalization__summary-chip';
      const label = document.createElement('small');
      const text = document.createElement('span');
      label.textContent = value ? 'Destination URL' : 'Required';
      text.textContent = value || 'Enter the destination URL';
      chip.append(label, text);
      summary.append(chip);
    };

    const gateCheckout = (valid) => {
      const productForm = form.closest('product-form-component');
      if (!productForm) return;
      const add = productForm.querySelector('[ref="addToCartButton"]');
      const available = !(add instanceof HTMLButtonElement) || !add.disabled;
      productForm.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]').forEach((box) => {
        if (!(box instanceof HTMLElement)) return;
        if (!valid) {
          if (!box.hidden) box.dataset.nmvxNfcOwned = 'true';
          box.hidden = true;
        } else if (box.dataset.nmvxNfcOwned === 'true') {
          if (available) box.hidden = false;
          delete box.dataset.nmvxNfcOwned;
        }
      });
    };

    const api = {
      place: placeCard,
      sync(trim = false) {
        if (trim) input.value = input.value.trim();
        const value = input.value.trim();
        const valid = validUrl(value);
        const msg = valid ? '' : message(value);
        input.setCustomValidity(msg);
        card.dataset.complete = String(valid);

        const ready = readiness();
        if (ready) {
          ready.textContent = valid ? 'Ready to order' : 'Personalization required';
          ready.dataset.state = valid ? 'ready' : 'required';
        }
        if (valid || !attempted) {
          input.removeAttribute('aria-invalid');
          if (error) { error.textContent = ''; error.hidden = true; }
        } else {
          input.setAttribute('aria-invalid', 'true');
          if (error) { error.textContent = msg; error.hidden = false; }
        }
        setSummary(value, valid);
        gateCheckout(valid);
        return { valid, value };
      },
      queue() {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          try {
            placeCard();
            api.sync();
          } catch (error) { console.error('[NAMVEXO NFC] Safe reconciliation error.', error); }
        });
      },
      settle() {
        placeCard();
        api.queue();
        if (follow) return;
        follow = requestAnimationFrame(() => {
          follow = requestAnimationFrame(() => {
            follow = 0;
            try {
              placeCard();
              api.sync();
            } catch (error) { console.error('[NAMVEXO NFC] Safe follow-up error.', error); }
          });
        });
      },
      report() {
        attempted = true;
        card.dataset.attempted = 'true';
        if (api.sync(true).valid) return;
        input.reportValidity();
        card.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        requestAnimationFrame(() => input.focus({ preventScroll: true }));
      },
      destroy() {
        if (frame) cancelAnimationFrame(frame);
        if (follow) cancelAnimationFrame(follow);
        aborter.abort();
        cards.delete(card);
      },
    };

    input.addEventListener('input', () => {
      attempted = false;
      card.removeAttribute('data-attempted');
      api.sync();
      api.queue();
    }, { signal: aborter.signal });
    input.addEventListener('blur', () => api.sync(true), { signal: aborter.signal });
    ['change', 'product:update', 'variant:change'].forEach((name) => root.addEventListener?.(name, api.settle, { signal: aborter.signal }));

    cards.set(card, api);
    api.sync();
    api.settle();
  };

  const initialize = (root = document) => {
    const list = root instanceof Element && root.matches(SEL) ? [root] : root.querySelectorAll?.(SEL) || [];
    list.forEach((card) => { try { init(card); } catch (error) { console.error('[NAMVEXO NFC] Safe init error.', error); } });
  };

  const boot = () => {
    installGuards();
    if (!lifecycle) {
      lifecycle = true;
      document.addEventListener('shopify:section:load', (event) => initialize(event.target));
      document.addEventListener('shopify:section:unload', (event) => {
        const root = event.target;
        if (!(root instanceof Element)) return;
        const list = root.matches(SEL) ? [root] : root.querySelectorAll(SEL);
        list.forEach((card) => cards.get(card)?.destroy());
      });
    }
    initialize();
  };

  window[KEY] = boot;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
